import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import { characters } from '@/mocks/characters';
import type { Drama } from '@/types';
import DramaCard from '@/components/base/DramaCard';
import SectionHeading from '@/components/base/SectionHeading';
import ShareDialog from '@/components/base/ShareDialog';
import EmptyState from '@/components/base/EmptyState';
import { useFavorites } from '@/hooks/useFavorites';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useToast } from '@/components/base/ToastProvider';

export default function DetailPage() {
  const { id = '' } = useParams();
  const drama = (dramas as Drama[]).find((item) => item.id === id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { showToast } = useToast();
  const [shareOpen, setShareOpen] = useState(false);

  const related = useMemo(() => {
    if (!drama) return [];
    return (dramas as Drama[])
      .filter((item) => item.id !== drama.id)
      .map((item) => {
        const sameGenre = item.genres.filter((g) => drama.genres.includes(g)).length;
        const sameActor = item.actors.filter((a) => drama.actors.includes(a)).length;
        return { item, score: sameGenre * 2 + sameActor * 3 };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((entry) => entry.item);
  }, [drama]);

  const relatedCharacters = (characters as { id: string; name: string; dramaId: string; role: string; avatar: string }[]).filter(
    (character) => character.dramaId === id,
  );

  useDocumentMeta({
    title: drama
      ? `${drama.title} - 影视详情与剧情介绍 | 善良的女秘书`
      : '影视详情 | 善良的女秘书',
    description: drama
      ? `${drama.title}（${drama.year}·${drama.region}）剧情简介：${drama.synopsis.slice(0, 80)}… 主要演员：${drama.actors.join('、')}。`
      : '善良的女秘书影视详情页。',
    keywords: drama
      ? `${drama.title},${drama.genres.join(',')},剧情介绍,演员表,善良的女秘书`
      : '善良的女秘书',
    type: 'article',
  });

  if (!drama) {
    return (
      <div className="w-full px-4 py-20 md:px-8 lg:px-12">
        <EmptyState
          title="未找到该影视作品"
          description="该作品可能已下线或链接有误，去分类页看看吧。"
          actionLabel="返回分类页"
          onAction={() => {
            window.REACT_APP_NAVIGATE('/category/tv');
          }}
        />
      </div>
    );
  }

  const favorite = isFavorite(drama.id);

  const metaInfo = [
    { label: '主演', value: drama.actors.join(' / ') },
    { label: '导演', value: drama.director },
    { label: '类型', value: drama.genres.join(' · ') },
    { label: '地区', value: drama.region },
    { label: '上映年份', value: String(drama.year) },
    { label: '集数', value: drama.type === 'tv' ? `全 ${drama.episodes} 集` : '电影' },
    { label: '更新时间', value: drama.updateTime },
  ];

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-50">
        <div className="px-4 py-6 md:px-8 lg:px-12">
          <nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="whitespace-nowrap transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <Link
              to={`/category/${drama.type}`}
              className="whitespace-nowrap transition-colors hover:text-primary-600"
            >
              {drama.type === 'tv' ? '电视剧' : '电影'}
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="line-clamp-1 text-foreground-800">{drama.title}</span>
          </nav>

          <div className="flex flex-col gap-8 lg:flex-row">
            <div className="mx-auto w-48 shrink-0 sm:w-56 lg:mx-0 lg:w-64">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
                <img
                  src={drama.cover}
                  alt={`${drama.title} 海报`}
                  title={`${drama.title} 影视详情`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                  {drama.title}
                </h1>
                <span className="rounded-md bg-primary-100 px-2.5 py-1 text-sm font-semibold text-primary-700">
                  {drama.rating.toFixed(1)} 分
                </span>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
                <span className="rounded bg-background-200 px-2 py-0.5">
                  {drama.type === 'tv' ? '电视剧' : '电影'}
                </span>
                <span>{drama.year}</span>
                <span>·</span>
                <span>{drama.region}</span>
                <span>·</span>
                <span>{drama.genres.join(' / ')}</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {drama.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2">
                {metaInfo.map((info) => (
                  <div key={info.label} className="flex gap-3 text-sm">
                    <dt className="w-16 shrink-0 text-foreground-400">{info.label}</dt>
                    <dd className="min-w-0 flex-1 text-foreground-800">{info.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    const next = toggleFavorite(drama.id);
                    showToast(next ? '已加入收藏' : '已取消收藏');
                  }}
                  className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                    favorite
                      ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                      : 'border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={favorite ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  {favorite ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-10 md:px-8 lg:px-12">
        <section className="max-w-4xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950">
            剧情简介
          </h2>
          <p className="mt-4 text-[15px] leading-loose text-foreground-700">
            {drama.synopsis}
          </p>
          <p className="mt-4 text-[15px] leading-loose text-foreground-700">
            本片由 <strong className="font-medium text-foreground-900">{drama.director}</strong> 执导，{drama.actors.join('、')} 等联袂出演。作品以细腻的笔触描绘人物在现实处境中的选择与成长，在
            {drama.genres.join('、')}的类型表达中，呈现出独特的情感张力，值得一看。
          </p>
        </section>

        {relatedCharacters.length > 0 && (
          <section className="mt-12">
            <SectionHeading
              eyebrow="Character"
              title="角色分析专题"
              description="深入了解剧中核心角色的性格、关系与成长轨迹。"
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {relatedCharacters.map((character) => (
                <Link
                  key={character.id}
                  to={`/character/${character.id}`}
                  className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                >
                  <span className="h-14 w-14 shrink-0 overflow-hidden rounded-full bg-background-200">
                    <img
                      src={character.avatar}
                      alt={character.name}
                      className="h-full w-full object-cover object-top"
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {character.name}
                    </h3>
                    <p className="mt-0.5 line-clamp-1 text-xs text-foreground-500">
                      {character.role}
                    </p>
                  </div>
                  <i className="ri-arrow-right-line text-foreground-300 transition-transform group-hover:translate-x-1 group-hover:text-primary-500"></i>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-12">
          <SectionHeading
            eyebrow="Recommend"
            title="相关推荐"
            description="根据题材与演员为你推荐相似作品。"
          />
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 lg:grid-cols-5">
            {related.map((item) => (
              <DramaCard key={item.id} drama={item} />
            ))}
          </div>
        </section>
      </div>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={drama.title} />
    </div>
  );
}